'use client'

import { ButtonImage } from '@/components/common/Button'
import { usePage } from '@/components/providers/PageProvider'
import { CARD_IMAGE_BASE } from '@/defs/misc'
import { COLOR_ID_GROUP_LABELS, COLOR_ID_LABELS, type ColorIdType } from '@/defs/mtg'
import { type RouteType } from '@/defs/route'
import { useLinkInternal } from '@/hooks/useLinkInternal'

import styles from './ChooserColorId.module.scss'

type Props = {
  label?: string
} & RouteType

const IMAGE_MAP: Record<ColorIdType, string> = {
  abzan: `${CARD_IMAGE_BASE}/art/front/2/b/2b74bb81-fb9a-40e5-a941-e517430b52f5.webp`,
  azorius: `${CARD_IMAGE_BASE}/art/front/e/0/e056b55f-82ed-4fe0-ab0c-bb20fa4a218a.webp`,
  bant: `${CARD_IMAGE_BASE}/art/front/7/3/7363f1fb-9af3-4212-921f-d59533faf0e5.webp`,
  boros: `${CARD_IMAGE_BASE}/art/front/8/b/8b4e2642-3c87-4708-b9b4-2e7f7359ac7d.webp`,
  colorless: `${CARD_IMAGE_BASE}/art/front/3/9/396bbb7d-ae61-4d8d-b931-9ed2f712832e.webp`,
  dimir: `${CARD_IMAGE_BASE}/art/front/5/b/5b8170dc-6a90-46fc-9989-7575f3d402b5.webp`,
  'dune-brood': `${CARD_IMAGE_BASE}/art/front/e/1/e1fee302-b37b-4057-934f-cf212e6ecc17.webp`,
  esper: `${CARD_IMAGE_BASE}/art/front/a/2/a2c56479-4bee-4edb-80d7-4af010b7c793.webp`,
  'five-color': `${CARD_IMAGE_BASE}/art/front/2/7/27e36832-09ca-4070-8511-94580014c3ed.webp`,
  'glint-eye': `${CARD_IMAGE_BASE}/art/front/5/f/5fc770b6-03ac-45c3-a2ca-bd508096384e.webp`,
  golgari: `${CARD_IMAGE_BASE}/art/front/4/5/45b92924-baa1-4c9b-9932-9a5eda8f3446.webp`,
  grixis: `${CARD_IMAGE_BASE}/art/front/5/4/54f449ff-4025-465e-9ec5-a5cf42c4c9d3.webp`,
  gruul: `${CARD_IMAGE_BASE}/art/front/6/9/69be21b4-c613-47c6-ba57-f4785861af3e.webp`,
  'ink-treader': `${CARD_IMAGE_BASE}/art/front/9/a/9ac9c12b-5135-4124-b01b-336560907940.webp`,
  izzet: `${CARD_IMAGE_BASE}/art/front/b/6/b66daa94-d367-4812-9f18-f35378c1febb.webp`,
  jeskai: `${CARD_IMAGE_BASE}/art/front/0/2/02138fbb-3962-4348-8d31-faaefba0b8b2.webp`,
  jund: `${CARD_IMAGE_BASE}/art/front/7/5/75fdce80-e338-4a50-bdc6-786511feaeef.webp`,
  mardu: `${CARD_IMAGE_BASE}/art/front/7/4/748e6a61-9c1f-4225-9f04-e54002f63ac3.webp`,
  'mono-black': `${CARD_IMAGE_BASE}/art/front/e/1/e1efb0d3-2c72-46ff-bdc1-1069967365a0.webp`,
  'mono-blue': `${CARD_IMAGE_BASE}/art/front/6/7/67652446-6d12-4e2a-bb51-ba685f2e79d1.webp`,
  'mono-green': `${CARD_IMAGE_BASE}/art/front/e/3/e3c2c66c-f7f0-41d5-a805-a129aeaf1b75.webp`,
  'mono-red': `${CARD_IMAGE_BASE}/art/front/2/5/25ac5405-df7b-4097-914a-022cb18e20d4.webp`,
  'mono-white': `${CARD_IMAGE_BASE}/art/front/b/b/bb89c14b-028a-4bef-ae94-703198ef0376.webp`,
  naya: `${CARD_IMAGE_BASE}/art/front/2/6/26d40e03-6de4-4373-9fbf-04c1dd79e995.webp`,
  orzhov: `${CARD_IMAGE_BASE}/art/front/8/c/8c542ea4-98c3-4c2d-9066-205ab7aa697a.webp`,
  rakdos: `${CARD_IMAGE_BASE}/art/front/6/d/6da63cc5-4624-4491-abd9-9b600c3fefe2.webp`,
  selesnya: `${CARD_IMAGE_BASE}/art/front/6/c/6cdd2a74-63b3-4ff2-9c5a-a85dee63c3c9.webp`,
  simic: `${CARD_IMAGE_BASE}/art/front/3/c/3c750d5a-f743-41ff-b5ba-02025ca0bec2.webp`,
  sultai: `${CARD_IMAGE_BASE}/art/front/c/c/cc520518-2063-4b57-a0d4-10cf62a7175e.webp`,
  temur: `${CARD_IMAGE_BASE}/art/front/a/2/a249b1f4-2b22-4b67-a207-e0c4ae95d2e1.webp`,
  'witch-maw': `${CARD_IMAGE_BASE}/art/front/4/a/4a2dfd72-a8f4-4e43-b5de-caf6a129a51a.webp`,
  'yore-tiller': `${CARD_IMAGE_BASE}/art/front/d/4/d477d733-2360-4f1d-9ecd-cdedc2e8a7ae.webp`,
}

export const ChooserColorId = ({ label, ...props }: Props) => {
  const { buildRelative } = useLinkInternal()
  const { page } = usePage()

  const colorIdMap = page?.optionsFilters?.colorIdMap || {}

  return (
    <ul className={styles.container}>
      {COLOR_ID_GROUP_LABELS.map((group, groupKey) => (
        <li key={groupKey}>
          <p className={styles.groupLabel}>{group.label}</p>
          <ul className={styles.buttons}>
            {group.cis.map((ci, ciKey) => (
              <li className={styles.option} key={ciKey}>
                <ButtonImage
                  {...props}
                  as='LinkRoute'
                  colorId={ci}
                  colors={COLOR_ID_LABELS[ci].colors}
                  count={colorIdMap[ci]}
                  href={buildRelative({ ...props, colorId: ci })}
                  image={IMAGE_MAP[ci]}
                  label={label}
                  nowrap
                >
                  {COLOR_ID_LABELS[ci].label}
                </ButtonImage>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}
